import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AddIcon, WalletIcon } from 'icons/materialIcons';
import EventBadgesModal from './EventBadgesModal';
import Wallet from './Wallet';
import { useQRCodeContext } from 'contexts/QrCodeContext';
import QRCodeColorPicker from './QRCodeColorPicker';
import QrCodeCustomLogo from './QrCodeCustomLogo';
import { useParams, useNavigate } from 'react-router-dom';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';

const QRCodeContainer = () => {
    const iconColor = "#828282";
    const outlineColor = '#dedede';
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLayout, setSelectedLayout] = useState('');
    const [showWallet, setShowWallet] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [hasChanges, setHasChanges] = useState(false);
    const [open, setOpen] = useState(false);  // for UpgradeSnackBar
    const { cardId } = useParams();
    const navigate = useNavigate();

    const {
        qrCodeState,
        setQRCodeColor,
        setQRCodeLogoUrl,
        clearQRCodeSettings
    } = useQRCodeContext();

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubscribe = () => {
        handleClose();
        navigate(`/dashboard/subscribe`);
    };

    useEffect(() => {
        if (cardId && qrCodeState[cardId]) {
            const currentColor = qrCodeState[cardId].contextQrCodeColor;
            const currentLogo = qrCodeState[cardId].contextQrCodeLogoUrl;

            setHasChanges(
                (currentColor && currentColor !== '#000000') ||
                (currentLogo !== null && currentLogo !== undefined)
            );
        }
    }, [cardId, qrCodeState]);

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleSelectionChange = (layout) => {
        setSelectedLayout(layout);
    };

    const handleAddToWallet = () => {
        setShowWallet(true);
    };

    const handleCloseWallet = () => {
        setShowWallet(false);
    };

    const handleCancel = () => {
        if (cardId) {
            setQRCodeColor(cardId, '#000000');
            setQRCodeLogoUrl(cardId, null);
            clearQRCodeSettings();
            setRefreshKey(prev => prev + 1);
            setHasChanges(false);
        }
    };

    // Commented out as part of temporary change to hide upgrade features
    // const handleSave = () => {
    //     setOpen(true);  // Show upgrade snackbar
    // };

    // Updated cancel button styles - now takes position of save button for better UI alignment
    const cancelButtonStyles = {
        textTransform: 'none',
        borderRadius: '8px',
        minWidth: { xs: '120px', sm: '150px' },
        marginTop: 4,
        marginLeft: 2,
        ...(hasChanges ? {
            backgroundColor: 'black', // Using the same style as save button since it's taking its place
            color: 'white',
            '&:hover': {
                backgroundColor: 'black',
            }
        } : {
            backgroundColor: '#e0e0e0',
            color: '#a9a9ac',
            '&:hover': {
                backgroundColor: '#e0e0e0',
            }
        })
    };

    // Commented out as part of temporary change to hide upgrade features
    // const saveButtonStyles = {
    //     textTransform: 'none', 
    //     borderRadius: '8px', 
    //     minWidth: { xs: '120px', sm: '150px' }, 
    //     marginTop: 4,
    //     marginLeft: 2,
    //     ...(hasChanges ? {
    //         backgroundColor: 'black',
    //         color: 'white',
    //         '&:hover': {
    //             backgroundColor: 'black',
    //         }
    //     } : {
    //         backgroundColor: '#e0e0e0',
    //         color: '#a9a9ac',
    //         '&:hover': {
    //             backgroundColor: '#e0e0e0',
    //         }
    //     })
    // };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    padding: 2,
                    width: '100%',
                    maxWidth: '500px',
                    margin: '15px auto',
                }}
            >
                {/* ... rest of your JSX remains the same ... */}
                <Typography
                    variant="h6"
                    sx={{
                        marginBottom: '20px',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                    }}
                >
                    QR Code
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ color: iconColor }} />}
                        sx={{
                            textTransform: 'none',
                            borderColor: outlineColor,
                            borderRadius: '8px',
                            minWidth: { xs: '120px', sm: '150px' },
                            color: 'inherit',
                        }}
                        onClick={handleOpenModal}
                    >
                        Create Event Badge
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<WalletIcon sx={{ color: iconColor }} />}
                        sx={{
                            textTransform: 'none',
                            borderColor: outlineColor,
                            borderRadius: '8px',
                            minWidth: { xs: '120px', sm: '150px' },
                            color: 'inherit',
                        }}
                        onClick={handleAddToWallet}
                    >
                        Add To Wallet
                    </Button>
                </Box>

                {showWallet && <Wallet onClose={handleCloseWallet} />}

                <EventBadgesModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    onSelectionChange={handleSelectionChange}
                    selectedLayout={selectedLayout}
                />

                <Box sx={{ marginTop: 6 }}>
                    <QRCodeColorPicker
                        key={`color-${refreshKey}`}
                        forceReset={refreshKey}
                    />
                </Box>

                <Box sx={{ marginTop: 4 }}>
                    <QrCodeCustomLogo
                        key={`logo-${refreshKey}`}
                        forceReset={refreshKey}
                    />
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    width: '100%'
                }}>
                    {/* Only showing the Cancel button in place of the Save button */}
                    {/* Save QRCode button hidden - temporary change to hide upgrade features */}
                    <Button
                        variant="contained"
                        sx={cancelButtonStyles}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>

            {/* UpgradeSnackBar still included but will never be shown since handleSave() is not called */}
            <UpgradeSnackBar
                open={open}
                handleClose={handleClose}
                handleSubscribe={handleSubscribe}
            />
        </>
    );
};

export default QRCodeContainer;